import { Accordion, Col, Row } from "react-bootstrap";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { ICartProductAdd } from "../../models/api/ICartProduct";
import { IOrderRow } from "../../models/api/IOrderRow";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useAddCartProductMutation, useGetProductsQuery } from "../../api/ecosternApi";
import { selectCurrentClientId } from "../../store/slices/userSlice";
import { getTotalSum, isProductArchived } from "../../utils/product.utils";
import { LoadingSpinnerButton } from "../ui/buttons/loading-spinner-button";
import { CartzillaIcon, CartzillaIconName } from "../ui/icons/cartzilla-icon";
import { NoImage } from "../ui/images/no-image-placeholder/no-image";
import { ArchivedProductIndicator } from "../ui/images/product-image/archived-product-icon";
import { ProductImage } from "../ui/images/product-image/product-image";
import { TableRegularRow } from "../ui/table/table-regular-row";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { toggleProductModal } from "store/slices/uiSlice";
import { IProduct } from "models/api/IProduct";
import { getCartProductById } from "store/utils/cartUtil";

interface IProps {
    row: IOrderRow;
}

export const OrderAccordionRow = ({ row }: IProps) => {
    const dispatch = useAppDispatch();
    const [postAddToCart] = useAddCartProductMutation();
    const currentClientId = useAppSelector(selectCurrentClientId);
    const selectedClientId = useAppSelector(selectCurrentClientId);
    const { data: allProducts } = useGetProductsQuery(selectedClientId ?? skipToken);
    const intl = useIntl();

    const isTransport = row.productId == -4;

    const onAddProductToCart = (productId?: number) => {
        if (!currentClientId) return;

        const _productId = productId ?? row.productId;
        const existingCartProduct = getCartProductById(_productId);

        const newQuantity = row.quantity + (existingCartProduct?.quantity ?? 0);

        const cartProduct: ICartProductAdd = {
            productId: _productId,
            quantity: newQuantity,
        };
        postAddToCart(cartProduct);
    };

    const getPotentialAnalogues = (product?: IProduct | null) => {
        const analogues = allProducts?.filter((p) => product?.analogueProducts?.includes(p.id));

        if (!analogues?.length) {
            return;
        }

        return (
            <Col sm={12} className={"mt-3"}>
                <Accordion defaultActiveKey={`${row.productId}-analogues`} className={"accordion-sm"}>
                    <Accordion.Item eventKey={`${row.productId}-analogues`}>
                        <Accordion.Header>
                            <span className={"fs-sm"}>
                                <FormattedMessage id={"shoppingCart.cartProduct.potentialAnalogues"} />
                            </span>
                        </Accordion.Header>
                        <Accordion.Body className={"py-2"}>
                            <Row className={"gap-1"}>
                                <Col sm={12}>
                                    {allProducts
                                        ?.filter((p) => row.product?.analogueProducts?.includes(p.id))
                                        ?.map((product) => (
                                            <Col
                                                key={`${row.productId}-analogues-${product.id}`}
                                                xs={12}
                                                className={"d-flex gap-1 align-items-center justify-content-between"}
                                            >
                                                <div
                                                    onClick={() => dispatch(toggleProductModal({ product, hasParams: false }))}
                                                    className={"d-flex align-items-center gap-3 cursor-pointer "}
                                                >
                                                    <ProductImage id={product.id} item={product} size={"sm"} useZoom={false} />
                                                    <span className={"fs-md"}>{product.name}</span>
                                                </div>
                                                <div className={"px-2"}>
                                                    <LoadingSpinnerButton
                                                        variant={"secondary"}
                                                        size={"sm"}
                                                        isLoading={false}
                                                        icon={<CartzillaIcon icon={CartzillaIconName.Cart} />}
                                                        className={"border-0"}
                                                        onClick={() => onAddProductToCart(product.id)}
                                                    />
                                                </div>
                                            </Col>
                                        ))}
                                </Col>
                            </Row>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Col>
        );
    };

    return (
        <TableRegularRow key={row.productId}>
            <Col md={5}>
                <Row className={"align-items-center"}>
                    <Col xs={4} md={3}>
                        {!isTransport ? <ProductImage id={row.productId} item={row.product} size={"lg"} /> : <NoImage size={"lg"} />}
                    </Col>
                    <Col xs={8} md={9} className={"d-flex flex-column"}>
                        <div>{row.product?.name}</div>
                        {(!row.product || (!row.product.isAllowedForWeb && !isTransport)) && (
                            <ArchivedProductIndicator message={intl.formatMessage({ id: "orderContents.productArchived" })} variant={"text"} />
                        )}
                        {row.product?.isDiscontinued && row.product.isAllowedForWeb && (
                            <div className={`d-flex align-items-center text-warning`}>
                                <CartzillaIcon icon={CartzillaIconName.Announcement} className={"text-warning"} />
                                <span className={"ms-2"}>&nbsp; {intl.formatMessage({ id: "orderContents.productDiscontinued" })}</span>
                            </div>
                        )}
                    </Col>
                </Row>
            </Col>
            <Col md={6} className={"mt-3 mt-md-0"}>
                <Row className={"align-items-center"}>
                    <Col>
                        <div className={"fw-bold d-md-none"}>
                            <FormattedMessage id={"orderContents.code"} />
                        </div>
                        {row.product?.code || row.productId}
                    </Col>
                    <Col>
                        <div className={"fw-bold d-md-none"}>
                            <FormattedMessage id={"orderContents.quantity"} />
                        </div>
                        {row.quantity}
                    </Col>
                    <Col>
                        <div className={"fw-bold d-md-none"}>
                            <FormattedMessage id={"orderContents.price"} />
                        </div>
                        <FormattedNumber value={Math.round(row.price * 100) / 100} style={"currency"} currency={"EUR"} />
                    </Col>
                    <Col className={"d-none d-md-block"}>
                        <FormattedNumber value={getTotalSum(row.quantity, row.price)} style={"currency"} currency={"EUR"} />
                    </Col>
                </Row>
            </Col>
            <Col sm={12} md={1} className={"d-flex justify-content-lg-end"}>
                {!isTransport && !isProductArchived(row.product) && (
                    <LoadingSpinnerButton
                        variant={"secondary"}
                        size={"sm"}
                        isLoading={false}
                        icon={<CartzillaIcon icon={CartzillaIconName.Cart} className={"me-md-n2"} />}
                        className={"border-0 mt-3 mt-md-0"}
                        onClick={() => onAddProductToCart()}
                        style={{ marginRight: "1.25rem" }}
                    >
                        <span className={"d-md-none"}>Lisa</span>
                    </LoadingSpinnerButton>
                )}
            </Col>
            {(isProductArchived(row.product) || row.product?.isDiscontinued) && !isTransport ? getPotentialAnalogues(row.product) : null}
        </TableRegularRow>
    );
};
